import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Row, Stack } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { SchedulesContext } from 'features/Responder/features/Schedules/contexts/SchedulesContext';
import { RunHistory } from 'features/Responder/features/Schedules/features/Runs/features/RunHistory';
import ScheduleTelemetry from 'features/Responder/features/Schedules/features/ScheduleTelemetry';
import {
  isRunOpen,
  resolveRunScheduleId,
  resolveScheduleEntityId,
  resolveScheduleEntityType,
  resolveScheduleId,
  resolveScheduleZoneIds,
} from 'features/Responder/features/Schedules/utils';
import { useTours } from 'features/Responder/features/Tours';
import TourTelemetry from 'features/Responder/features/Tours/features/TourTelemetry';
import { useZones } from 'features/Responder/features/Zones';
import './telemetry-tabs.css';

const SCOPE_KEYS = ['default', 'tour', 'zone'];

const SCOPE_LABELS = {
  default: 'Default View',
  tour: 'Tour Only',
  zone: 'Zone Only',
};

const getFirstQueryValue = (params, keys = []) => {
  for (const key of keys) {
    const value = params.get(key);
    if (value != null && `${value}`.trim() !== '') return value;
  }
  return null;
};

const normalizeScope = (value) => {
  if (!value) return null;
  const normalized = `${value}`
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, '-');
  if (['default', 'schedule', 'all'].includes(normalized)) return 'default';
  if (['tour', 'tour-only', 'tours'].includes(normalized)) return 'tour';
  if (['zone', 'zone-only', 'zones'].includes(normalized)) return 'zone';
  return null;
};

const normalizePanelTab = (value) => {
  if (!value) return null;
  const normalized = `${value}`
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, '-');
  if (['history', 'run-history', 'runhistory', 'runs'].includes(normalized)) return 'history';
  if (['telemetry', 'live', 'overview', 'status'].includes(normalized)) return 'telemetry';
  return null;
};

const normalizeScheduleDetailTab = (value) => {
  if (!value) return null;
  const normalized = `${value}`
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, '-');
  if (normalized === 'overview') return 'overview';
  if (normalized === 'live') return 'live';
  return null;
};

const resolveNodeZoneId = (node) =>
  node?.zone?.id ??
  node?.zoneId ??
  node?.zone_id ??
  node?.geometry?.zoneId ??
  node?.geometry?.zone_id ??
  null;

const resolveTourZoneIds = (tour, { checkpoints = [], startLocation = null } = {}) => {
  if (!tour && !Array.isArray(checkpoints) && !startLocation) return [];
  const ids = [];
  const pushId = (value) => {
    if (value == null || value === '') return;
    const key = `${value}`;
    if (!ids.some((id) => `${id}` === key)) ids.push(value);
  };

  pushId(startLocation?.zoneId ?? startLocation?.zone_id ?? startLocation?.geometry?.zoneId);
  pushId(resolveNodeZoneId(tour?.originNode));

  const lists = [
    tour?.nodes,
    tour?.stops,
    tour?.checkpoints,
    Array.isArray(checkpoints) ? checkpoints : [],
  ];
  lists.forEach((list) => {
    if (!Array.isArray(list)) return;
    list.forEach((node) => pushId(resolveNodeZoneId(node)));
  });

  return ids;
};

const TabButton = ({ active, onClick, children }) => (
  <Button
    size="sm"
    variant={active ? 'primary' : 'outline-light'}
    onClick={onClick}
    className={`px-3 responder-telemetry-tab-btn ${
      active ? 'responder-telemetry-tab-btn--active' : ''
    }`}
  >
    {children}
  </Button>
);

const ZoneTelemetrySummary = ({
  zone,
  zoneId = null,
  zoneCount = 0,
  linkedSchedules = 0,
  totalRuns = 0,
  activeRuns = 0,
}) => {
  if (!zone && zoneId == null) {
    return <div className="text-muted">Select a zone to view telemetry and activity history.</div>;
  }

  const zoneLabel = zone?.name || (zone?.id != null ? `Zone #${zone.id}` : `Zone #${zoneId}`);

  return (
    <Stack gap={3}>
      <div>
        <div className="text-muted text-uppercase small">Zone telemetry</div>
        <div className="fw-semibold fs-5">{zoneLabel}</div>
        <div className="small text-muted">
          Live zone metrics are not yet wired from backend telemetry.
          {zoneCount > 1 ? ` Showing 1 of ${zoneCount} zone checkpoints.` : ''}
        </div>
      </div>
      <Row className="g-2">
        <Col xs={12} md={4}>
          <div className="border rounded p-2 h-100">
            <div className="text-muted text-uppercase small">Linked schedules</div>
            <div className="fw-semibold">{linkedSchedules}</div>
          </div>
        </Col>
        <Col xs={12} md={4}>
          <div className="border rounded p-2 h-100">
            <div className="text-muted text-uppercase small">Activities</div>
            <div className="fw-semibold">{totalRuns}</div>
          </div>
        </Col>
        <Col xs={12} md={4}>
          <div className="border rounded p-2 h-100">
            <div className="text-muted text-uppercase small">Active activities</div>
            <div className="fw-semibold">{activeRuns}</div>
          </div>
        </Col>
      </Row>
    </Stack>
  );
};

export default function ResponderTelemetry({
  scrollHeight = null,
  preferBuilderData = false,
  allowedScopes = SCOPE_KEYS,
  defaultScope = 'default',
  showScopeTabs = null,
}) {
  const location = useLocation();
  const schedulesContext = useContext(SchedulesContext);
  const hasSchedulesContext = Boolean(schedulesContext);
  const schedules = Array.isArray(schedulesContext?.schedules) ? schedulesContext.schedules : [];
  const runs = Array.isArray(schedulesContext?.runs) ? schedulesContext.runs : [];
  const activeSchedule = schedulesContext?.activeEditSchedule || null;

  const { activeTourData, builderTourData, tours, checkpoints, startLocation } = useTours();
  const { zonesWithMeta, selectedZoneIds } = useZones();

  const queryState = useMemo(() => {
    const params = new URLSearchParams(location?.search || '');
    const rawTab = getFirstQueryValue(params, ['telemetryTab', 'telemetry_tab', 'tab']);
    const scope =
      normalizeScope(
        getFirstQueryValue(params, ['telemetryScope', 'telemetry_scope', 'scope', 'view']),
      ) || normalizeScope(rawTab);
    const panelTab = normalizePanelTab(rawTab) || 'telemetry';
    const detailTab =
      normalizeScheduleDetailTab(rawTab) ||
      normalizeScheduleDetailTab(
        getFirstQueryValue(params, ['telemetryDetail', 'telemetry_detail', 'detail']),
      ) ||
      'live';
    return { scope, panelTab, detailTab };
  }, [location?.search]);

  const normalizedAllowedScopes = useMemo(() => {
    const list = Array.isArray(allowedScopes) ? allowedScopes : [allowedScopes];
    const values = list
      .map(normalizeScope)
      .filter(Boolean)
      .filter((value, idx, arr) => arr.indexOf(value) === idx);
    return values.length ? values : ['default'];
  }, [allowedScopes]);

  const initialScope = useMemo(() => {
    const requested =
      queryState.scope || normalizeScope(defaultScope) || normalizedAllowedScopes[0];
    return normalizedAllowedScopes.includes(requested) ? requested : normalizedAllowedScopes[0];
  }, [defaultScope, normalizedAllowedScopes, queryState.scope]);

  const shouldShowScopeTabs = useMemo(() => {
    if (typeof showScopeTabs === 'boolean') return showScopeTabs;
    if (normalizedAllowedScopes.length <= 1) return false;
    return initialScope === 'default';
  }, [initialScope, normalizedAllowedScopes.length, showScopeTabs]);

  const showHistoryPanelTab = useMemo(
    () => !(normalizedAllowedScopes.length === 1 && normalizedAllowedScopes[0] === 'tour'),
    [normalizedAllowedScopes],
  );

  const [activeScope, setActiveScope] = useState(initialScope);
  const [activePanelTab, setActivePanelTab] = useState(queryState.panelTab);

  useEffect(() => {
    if (normalizedAllowedScopes.includes(activeScope)) return;
    setActiveScope(normalizedAllowedScopes[0]);
  }, [activeScope, normalizedAllowedScopes]);

  useEffect(() => {
    if (showHistoryPanelTab) return;
    if (activePanelTab !== 'history') return;
    setActivePanelTab('telemetry');
  }, [activePanelTab, showHistoryPanelTab]);

  const selectedTour = useMemo(() => {
    const baseTour = preferBuilderData && builderTourData ? builderTourData : activeTourData;
    if (baseTour) return baseTour;

    const scheduleTargetType = resolveScheduleEntityType(activeSchedule);
    if (scheduleTargetType !== 'tour') return null;
    const targetId = resolveScheduleEntityId(activeSchedule);
    if (targetId == null || !Array.isArray(tours)) return null;
    return tours.find((tour) => `${tour?.id}` === `${targetId}`) || null;
  }, [activeSchedule, activeTourData, builderTourData, preferBuilderData, tours]);

  const scheduleZoneIds = useMemo(() => resolveScheduleZoneIds(activeSchedule), [activeSchedule]);
  const tourZoneIds = useMemo(
    () => resolveTourZoneIds(selectedTour, { checkpoints, startLocation }),
    [checkpoints, selectedTour, startLocation],
  );
  const explicitSelectedZoneId =
    Array.isArray(selectedZoneIds) && selectedZoneIds.length ? selectedZoneIds[0] : null;
  const zoneScopeIds = useMemo(() => {
    if (explicitSelectedZoneId != null && explicitSelectedZoneId !== '') {
      return [`${explicitSelectedZoneId}`];
    }
    if (scheduleZoneIds.length) return scheduleZoneIds.map((id) => `${id}`);
    if (tourZoneIds.length) return tourZoneIds.map((id) => `${id}`);
    return [];
  }, [explicitSelectedZoneId, scheduleZoneIds, tourZoneIds]);

  const selectedZone = useMemo(() => {
    const resolvedZoneId = zoneScopeIds.length ? zoneScopeIds[0] : null;
    if (resolvedZoneId == null || !Array.isArray(zonesWithMeta)) return null;
    return zonesWithMeta.find((zone) => `${zone?.id}` === `${resolvedZoneId}`) || null;
  }, [zoneScopeIds, zonesWithMeta]);

  const tourScheduleIds = useMemo(() => {
    const targetId = selectedTour?.id;
    if (targetId == null || !Array.isArray(schedules)) return [];
    return Array.from(
      new Set(
        schedules
          .filter((schedule) => {
            if (resolveScheduleEntityType(schedule) !== 'tour') return false;
            return `${resolveScheduleEntityId(schedule)}` === `${targetId}`;
          })
          .map((schedule) => resolveScheduleId(schedule))
          .filter((value) => value != null)
          .map((value) => `${value}`),
      ),
    );
  }, [schedules, selectedTour?.id]);

  const zoneScheduleIds = useMemo(() => {
    if (!zoneScopeIds.length || !Array.isArray(schedules)) return [];
    const zoneLookup = new Set(zoneScopeIds.map((id) => `${id}`));
    return Array.from(
      new Set(
        schedules
          .filter((schedule) => {
            const scheduleZoneIds = resolveScheduleZoneIds(schedule);
            return scheduleZoneIds.some((zoneId) => zoneLookup.has(`${zoneId}`));
          })
          .map((schedule) => resolveScheduleId(schedule))
          .filter((value) => value != null)
          .map((value) => `${value}`),
      ),
    );
  }, [schedules, zoneScopeIds]);

  const defaultScheduleIds = useMemo(() => {
    const id = resolveScheduleId(activeSchedule);
    return id == null ? [] : [`${id}`];
  }, [activeSchedule]);

  const historyScheduleIds = useMemo(() => {
    if (activeScope === 'tour') return tourScheduleIds;
    if (activeScope === 'zone') return zoneScheduleIds;
    return defaultScheduleIds;
  }, [activeScope, defaultScheduleIds, tourScheduleIds, zoneScheduleIds]);

  const scopedRuns = useMemo(() => {
    if (!historyScheduleIds.length || !Array.isArray(runs)) return [];
    const lookup = new Set(historyScheduleIds.map((value) => `${value}`));
    return runs.filter((run) => {
      const scheduleId = resolveRunScheduleId(run);
      return scheduleId != null && lookup.has(`${scheduleId}`);
    });
  }, [historyScheduleIds, runs]);

  const activeScopedRunsCount = useMemo(
    () => scopedRuns.filter((run) => isRunOpen(run)).length,
    [scopedRuns],
  );

  const scopeLabel = SCOPE_LABELS[activeScope] || 'Telemetry';
  const zoneScopeLabelId = zoneScopeIds.length ? zoneScopeIds[0] : null;
  const scopeEntityLabel =
    activeScope === 'tour'
      ? selectedTour?.name ||
        (selectedTour?.id != null ? `Tour #${selectedTour.id}` : 'No tour selected')
      : activeScope === 'zone'
        ? selectedZone?.name ||
          (selectedZone?.id != null
            ? `Zone #${selectedZone.id}`
            : zoneScopeLabelId != null
              ? `Zone #${zoneScopeLabelId}`
              : 'No zone selected')
        : activeSchedule?.name ||
          (activeSchedule?.id != null ? `Schedule #${activeSchedule.id}` : 'No schedule selected');

  const containerStyle = scrollHeight
    ? { height: '100%', maxHeight: scrollHeight, overflowY: 'auto' }
    : {};

  return (
    <Card className="shadow-sm h-100" style={containerStyle}>
      <Card.Body className="d-flex flex-column gap-3">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div className="text-muted text-uppercase small">Responder telemetry</div>
            <div className="fw-semibold fs-5">{scopeLabel}</div>
            <div className="small text-muted">{scopeEntityLabel}</div>
          </div>
          <Badge bg={historyScheduleIds.length ? 'primary' : 'secondary'}>
            {historyScheduleIds.length} schedule{historyScheduleIds.length === 1 ? '' : 's'}
          </Badge>
        </div>

        {shouldShowScopeTabs && (
          <div className="d-flex gap-2 flex-wrap">
            {normalizedAllowedScopes.map((scope) => (
              <TabButton
                key={scope}
                active={activeScope === scope}
                onClick={() => setActiveScope(scope)}
              >
                {SCOPE_LABELS[scope] || scope}
              </TabButton>
            ))}
          </div>
        )}

        {showHistoryPanelTab && (
          <div className="d-flex gap-2 flex-wrap">
            <TabButton
              active={activePanelTab === 'telemetry'}
              onClick={() => setActivePanelTab('telemetry')}
            >
              Telemetry
            </TabButton>
            <TabButton
              active={activePanelTab === 'history'}
              onClick={() => setActivePanelTab('history')}
            >
              Activity History
            </TabButton>
          </div>
        )}

        {activePanelTab === 'telemetry' && (
          <div className="flex-grow-1" style={{ minHeight: 0, overflow: 'auto' }}>
            {activeScope === 'default' ? (
              <ScheduleTelemetry
                scrollHeight="100%"
                initialTab={queryState.detailTab}
                showHistoryTab={false}
              />
            ) : activeScope === 'tour' ? (
              <TourTelemetry scrollHeight="100%" preferBuilderData={preferBuilderData} />
            ) : (
              <ZoneTelemetrySummary
                zone={selectedZone}
                zoneId={zoneScopeLabelId}
                zoneCount={zoneScopeIds.length}
                linkedSchedules={zoneScheduleIds.length}
                totalRuns={scopedRuns.length}
                activeRuns={activeScopedRunsCount}
              />
            )}
          </div>
        )}

        {activePanelTab === 'history' && (
          <Stack gap={2} className="flex-grow-1" style={{ minHeight: 0 }}>
            {!hasSchedulesContext ? (
              <div className="text-muted">
                Activity history is unavailable in this view because schedules are not loaded.
              </div>
            ) : historyScheduleIds.length === 0 ? (
              <div className="text-muted">
                {activeScope === 'tour'
                  ? 'No schedules are linked to the selected tour.'
                  : activeScope === 'zone'
                    ? 'No schedules are linked to the selected zone.'
                    : 'Select a schedule to view activity history.'}
              </div>
            ) : (
              <RunHistory
                scheduleId={
                  activeScope === 'default' && historyScheduleIds.length === 1
                    ? historyScheduleIds[0]
                    : null
                }
                scheduleIds={activeScope === 'default' ? null : historyScheduleIds}
                enableRunSelection={true}
                showScheduleColumn={activeScope !== 'default'}
              />
            )}
          </Stack>
        )}
      </Card.Body>
    </Card>
  );
}
